import React from 'react';
import { Textfit } from 'react-textfit';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { hotIssueApi } from '../services/api';
import { HotIssue } from '../types';

interface HotIssueDetailProps {
  hotIssue: any;
  items: any[];
  loading: boolean;
  error: string | null;
}

const HotIssueDetailComponent: React.FC<HotIssueDetailProps> = ({ hotIssue, items, loading, error }) => {
  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  if (error || !hotIssue) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || '핫이슈를 찾을 수 없습니다.'}
        </Alert>
      </Container>
    );
  }
  return (
    <Container className="py-5">
      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="theme-card border-0 shadow-lg" style={{ borderRadius: '1.5rem', overflow: 'hidden', background: 'var(--card-bg)' }}>
            <Card.Header
              className="gradient-bg-primary text-white border-0"
              style={{
                borderTopLeftRadius: '1.5rem',
                borderTopRightRadius: '1.5rem',
                padding: '2.2rem 2.2rem 1.2rem 2.2rem',
                minHeight: 90,
                background: 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)'
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h2 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', fontSize: '2rem' }}>
                  <span className="d-none d-md-inline">{hotIssue.title}</span>
                  <Textfit
                    className="d-md-none"
                    mode="single"
                    min={12}
                    max={24}
                    style={{ width: '100%', display: 'block' }}
                  >
                    {hotIssue.title}
                  </Textfit>
                </h2>
                <button
                  className="btn btn-light border-0 shadow-sm px-3 py-2 d-flex align-items-center gap-2"
                  style={{ borderRadius: '999px', fontWeight: 600, fontSize: '1.05rem', color: '#ef4444', background: '#f3f4f6' }}
                  onClick={() => {
                    const shareUrl = `${window.location.origin}/hot-issue/${hotIssue.id}`;
                    navigator.clipboard.writeText(shareUrl);
                    toast('핫이슈 상세페이지 링크가 복사되었습니다!');
                  }}
                >
                  <i className="fas fa-share-alt"></i> 공유하기
                </button>
              </div>
            </Card.Header>
            <Card.Body style={{ padding: '2.2rem' }}>
              <div className="mb-4 p-4 rounded-4 shadow-sm bg-light d-flex align-items-center gap-3" style={{background: 'linear-gradient(90deg, #f8fafc 60%, #fde68a 100%)'}}>
                <i className="fas fa-fire fa-2x text-danger d-none d-md-block"></i>
                <i className="fas fa-fire text-danger d-md-none" style={{ fontSize: '1.2rem' }}></i>
                <span className="text-dark opacity-90 fw-semibold" style={{ lineHeight: 1.7 }}>
                  <span className="d-none d-md-inline fs-5">{hotIssue.description}</span>
                  <span className="d-md-none" style={{ fontSize: '0.95rem' }}>{hotIssue.description}</span>
                </span>
              </div>
              <div className="d-flex justify-content-end align-items-center mb-1" style={{ minHeight: '1.5rem' }}>
                <span className="text-muted small" style={{ fontSize: '0.98rem', letterSpacing: '-0.01em' }}>
                  {hotIssue.created_at ? new Date(hotIssue.created_at).toLocaleDateString('ko-KR') : ''}
                </span>
              </div>
              {/* 순위 데이터 테이블 */}
              <div className="table-responsive mb-4">
                {items.length > 0 ? (
                  <Table className="mb-0" hover style={{ borderRadius: '1rem', overflow: 'hidden', background: '#f9fafb' }}>
                    <thead style={{ background: 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)', color: 'white', fontSize: '1.08rem' }}>
                      <tr>
                        <th style={{ width: '10%' }}>순위</th>
                        <th style={{ width: '15%' }}>국기</th>
                        <th style={{ width: '25%' }}>이름</th>
                        <th style={{ width: '20%' }}>수치</th>
                        <th style={{ width: '30%' }}>설명</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} style={{ verticalAlign: 'middle' }}>
                          <td className="text-center fw-bold">
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.38em 0.7em',
                                fontSize: '1rem',
                                fontWeight: 700,
                                lineHeight: 1,
                                color: 'white',
                                background: 'linear-gradient(90deg, #ef4444 0%, #fde68a 100%)',
                                borderRadius: '999px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                marginRight: '0.5em',
                                marginBottom: '0.25em',
                                letterSpacing: '-0.01em'
                              }}
                            >
                              {item.rank}
                            </span>
                          </td>
                          <td className="text-center fs-4">
                            {item.flag ? item.flag : ''}
                          </td>
                          <td className="fw-bold">
                            {item.name}
                          </td>
                          <td>
                            <span className="fw-bold text-danger">
                              {item.value}
                            </span>
                            {item.unit && (
                              <small className="text-muted ms-1">
                                {item.unit}
                              </small>
                            )}
                          </td>
                          <td className="text-muted small">
                            {item.details}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted py-3">순위 데이터가 없습니다.</div>
                )}
              </div>
              {/* 트렌드, 인사이트 등 필요시 추가 영역 */}
              {hotIssue.trends && hotIssue.trends.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">
                    <i className="fas fa-trending-up me-2"></i>
                    트렌드 분석
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {hotIssue.trends.map((trend: any, index: any) => (
                      <span
                        key={index}
                        className={`badge px-3 py-2 rounded-pill bg-${trend.type === 'up' ? 'success' : 'danger'}`}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <i className={`fas fa-arrow-${trend.type} me-1`}></i>
                        {trend.keyword} {trend.percentage}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {hotIssue.insights && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">
                    <i className="fas fa-lightbulb me-2"></i>
                    인사이트
                  </h6>
                  <Card className="border-0" style={{ background: 'var(--gradient-primary)' }}>
                    <Card.Body className="text-white">
                      <p className="mb-0">{hotIssue.insights}</p>
                    </Card.Body>
                  </Card>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="d-flex align-items-center text-muted">
                  <Button variant="outline-secondary" onClick={() => window.history.back()}>
                    <i className="fas fa-arrow-left me-2"></i> 돌아가기
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const HotIssueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [hotIssue, setHotIssue] = React.useState<HotIssue | null>(null);
  const [items, setItems] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchHotIssue = async () => {
      try {
        setLoading(true);
        const issues = await hotIssueApi.getHotIssues();
        const found = issues.find((h: HotIssue) => h.id === Number(id));
        setHotIssue(found || null);
        if (found) {
          const data = await hotIssueApi.getHotIssueItems(found.id);
          setItems(data.slice(0, 10));
        } else {
          setItems([]);
        }
      } catch (err) {
        setError('핫이슈를 불러오는데 실패했습니다.');
        setHotIssue(null);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchHotIssue();
  }, [id]);

  return <HotIssueDetailComponent hotIssue={hotIssue} items={items} loading={loading} error={error} />;
};

export default HotIssueDetailPage; 